import {
  Bars3Icon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  CloudArrowUpIcon,
  MicrophoneIcon,
  VideoCameraIcon,
  ClockIcon,
} from "@heroicons/react/24/solid";
import axios from "axios";
import cn from "classnames";
import { DragEvent, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import Button from "../common/Button";
import {
  MAX_NARRATIVE_FILE_SIZE_IN_MB,
  MAX_INTERACTION_FILE_SIZE_IN_MB,
  MAX_AUDIO_DURATION_HOURS,
  MAX_AUDIO_DURATION_SECONDS,
} from "../common/maxFileSize";
import TextArea from "../Settings/TextArea";
import Toggle from "../Settings/Toggle";
import Tooltip from "../common/Tooltip";
import TemplateSelector from "../common/TemplateSelector";
import EvidenceModal, { EvidenceItem } from "./EvidenceModal";
import PreviousUploadsModal, {
  PreviousUploadItem,
} from "./PreviousUploadsModal";
import AudioPlayback from "./AudioPlayback";
import {
  FileConfig,
  MediaType,
  Speaker,
  UploadOptions,
  fileConfigFromLibraryItem,
  AUDIO_ACCEPT,
  defaultRecordingName,
  DOCUMENT_ACCEPT,
  DOCUMENT_MAX_MB,
  documentKindOf,
  getMediaTypeFromFile,
} from "./uploadFileConfig";
import SuggestionPoint from "../Editor/SuggestionPoint";

export type { FileConfig, UploadOptions };

const baseName = (name: string) => name.replace(/\.[^.]+$/, "");

const TRANSCRIBE_LANGUAGES = [
  { code: "en-US", name: "English (US)" },
  { code: "es-US", name: "Spanish (US)" },
  { code: "es-ES", name: "Spanish (Spain)" },
  { code: "fr-FR", name: "French" },
  { code: "fr-CA", name: "French (Canadian)" },
  { code: "pt-BR", name: "Portuguese (Brazilian)" },
  { code: "pt-PT", name: "Portuguese" },
  { code: "de-DE", name: "German" },
  { code: "it-IT", name: "Italian" },
  { code: "ja-JP", name: "Japanese" },
  { code: "ko-KR", name: "Korean" },
  { code: "zh-CN", name: "Chinese (Simplified)" },
  { code: "zh-TW", name: "Chinese (Traditional)" },
  { code: "zh-HK", name: "Chinese (Cantonese)" },
  { code: "ar-SA", name: "Arabic" },
  { code: "ar-AE", name: "Arabic (Gulf)" },
  { code: "hi-IN", name: "Hindi" },
  { code: "tl-PH", name: "Tagalog/Filipino" },
  { code: "vi-VN", name: "Vietnamese" },
  { code: "ru-RU", name: "Russian" },
  { code: "uk-UA", name: "Ukrainian" },
  { code: "pl-PL", name: "Polish" },
  { code: "nl-NL", name: "Dutch" },
  { code: "tr-TR", name: "Turkish" },
  { code: "th-TH", name: "Thai" },
  { code: "he-IL", name: "Hebrew" },
  { code: "fa-IR", name: "Farsi" },
  { code: "ro-RO", name: "Romanian" },
  { code: "hu-HU", name: "Hungarian" },
  { code: "cs-CZ", name: "Czech" },
  { code: "el-GR", name: "Greek" },
  { code: "bg-BG", name: "Bulgarian" },
  { code: "hr-HR", name: "Croatian" },
  { code: "sr-RS", name: "Serbian" },
  { code: "sk-SK", name: "Slovak" },
  { code: "sl-SI", name: "Slovenian" },
  { code: "da-DK", name: "Danish" },
  { code: "fi-FI", name: "Finnish" },
  { code: "no-NO", name: "Norwegian" },
  { code: "sv-SE", name: "Swedish" },
  { code: "id-ID", name: "Indonesian" },
  { code: "ms-MY", name: "Malay" },
  { code: "sw-KE", name: "Swahili" },
  { code: "af-ZA", name: "Afrikaans" },
  { code: "bn-IN", name: "Bengali" },
  { code: "gu-IN", name: "Gujarati" },
  { code: "kn-IN", name: "Kannada" },
  { code: "ml-IN", name: "Malayalam" },
  { code: "mr-IN", name: "Marathi" },
  { code: "ta-IN", name: "Tamil" },
  { code: "te-IN", name: "Telugu" },
  { code: "pa-IN", name: "Punjabi" },
  { code: "so-SO", name: "Somali" },
  { code: "ha-NG", name: "Hausa" },
  { code: "wo-SN", name: "Wolof" },
  { code: "zu-ZA", name: "Zulu" },
];

interface UploadModalProps {
  modalRef: React.RefObject<HTMLDialogElement>;
  onUpload: (files: FileConfig[], options: UploadOptions) => void;
  hasEvidenceIntegration?: boolean;
  // "library" = save the files to the Files page with no report: no report
  // settings, template, call sign, investigation, or footage analysis, and no
  // Bodycam / Previous Uploads shortcuts (the Files page is where those live).
  mode?: "report" | "library";
  hideReportSettings?: boolean;
  // Also accept PDFs, images, Word/text files and spreadsheets as source
  // material. Off for flows that only make sense with audio (add-to-report,
  // form fill).
  allowDocuments?: boolean;
  // When true, hide ALL report-related UI (template selector, detail level,
  // header text) and always submit reportTemplateId=null. Used by the form-fill
  // flow where the report template is irrelevant.
  hideReportTemplate?: boolean;
  suggestionsList?: {
    id: string;
    content: string;
    implemented: boolean | null;
  }[];
  placeholdersList?: string[];
  // Pre-fill the modal with files that already exist (the Files page's
  // "Create report"). Applied whenever a new array is passed in, replacing
  // whatever was queued.
  initialFiles?: FileConfig[];
  // Raw files dropped on the page behind the modal. Run through the same
  // validation as the modal's own drop zone and file picker.
  droppedFiles?: File[];
}

// Helper to check if file exceeds narrative size limit
const exceedsNarrativeSizeLimit = (file: File): boolean => {
  return file.size > MAX_NARRATIVE_FILE_SIZE_IN_MB * 1024 * 1024;
};

// Component to handle media preview with proper URL cleanup
const MediaPreview = ({
  file,
  mediaType,
}: {
  file: File;
  mediaType: MediaType;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const objectUrl = useMemo(() => URL.createObjectURL(file), [file]);

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  const renderPreview = () => {
    switch (mediaType) {
      case "audio":
        return (
          <audio controls className="w-full h-10" src={objectUrl}>
            Your browser does not support the audio element.
          </audio>
        );
      case "video":
        return (
          <video
            controls
            className="w-full max-h-48 rounded-lg bg-black"
            src={objectUrl}
          >
            Your browser does not support the video element.
          </video>
        );
      case "image":
        return (
          <img
            src={objectUrl}
            alt={file.name}
            className="w-full max-h-48 object-contain rounded-lg bg-gray-100 dark:bg-base-200"
          />
        );
      case "document":
        return (
          <div className="flex items-center gap-3 p-3 bg-gray-100 dark:bg-base-200 rounded-lg">
            <DocumentIcon className="h-8 w-8 text-base-content/60" />
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-base-content/80">{file.name}</p>
              <p className="text-xs text-base-content/60">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-base-content/80 hover:text-base-content"
      >
        {isExpanded ? (
          <EyeSlashIcon className="h-4 w-4" />
        ) : (
          <EyeIcon className="h-4 w-4" />
        )}
        {isExpanded
          ? mediaType === "audio"
            ? "Hide Audio Preview"
            : "Hide Preview"
          : mediaType === "audio"
            ? "Show Audio Preview"
            : "Show Preview"}
      </button>
      <div
        className={cn(
          "transition-all duration-200 ease-in-out overflow-hidden",
          isExpanded ? "max-h-60 opacity-100 mt-2" : "max-h-0 opacity-0",
        )}
      >
        {renderPreview()}
      </div>
    </div>
  );
};

// Component for Axon evidence video preview
const AxonPreview = ({ evidenceId }: { evidenceId: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-base-content/80 hover:text-base-content"
      >
        {isExpanded ? (
          <EyeSlashIcon className="h-4 w-4" />
        ) : (
          <EyeIcon className="h-4 w-4" />
        )}
        {isExpanded ? "Hide Video Preview" : "Show Video Preview"}
      </button>
      <div
        className={cn(
          "transition-all duration-200 ease-in-out overflow-hidden",
          isExpanded ? "max-h-60 opacity-100 mt-2" : "max-h-0 opacity-0",
        )}
      >
        <video
          controls
          className="w-full max-h-48 rounded-lg bg-black"
          src={`/api/evidence/${evidenceId}/stream`}
        >
          Your browser does not support the video element.
        </video>
      </div>
    </div>
  );
};

const PreviousUploadPreview = ({ fileId }: { fileId: string }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);

  useEffect(() => {
    if (isExpanded && !streamUrl) {
      axios
        .get(`/api/cases/files/${fileId}/stream`)
        .then((res) => setStreamUrl(res.data.streamUrl))
        .catch(() => setStreamUrl(null));
    }
  }, [isExpanded, fileId, streamUrl]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-base-content/80 hover:text-base-content"
      >
        {isExpanded ? (
          <EyeSlashIcon className="h-4 w-4" />
        ) : (
          <EyeIcon className="h-4 w-4" />
        )}
        {isExpanded ? "Hide Audio Preview" : "Show Audio Preview"}
      </button>
      <div
        className={cn(
          "transition-all duration-200 ease-in-out overflow-hidden",
          isExpanded ? "max-h-24 opacity-100 mt-2" : "max-h-0 opacity-0",
        )}
      >
        {streamUrl ? (
          <audio controls className="w-full h-10" src={streamUrl}>
            Your browser does not support the audio element.
          </audio>
        ) : (
          <div className="flex items-center gap-2 text-xs text-base-content/40">
            <span className="loading loading-spinner loading-xs"></span>
            Loading preview...
          </div>
        )}
      </div>
    </div>
  );
};

const UploadModal = ({
  modalRef,
  onUpload,
  hasEvidenceIntegration: hasEvidenceIntegrationProp = false,
  mode = "report",
  allowDocuments = false,
  hideReportSettings: hideReportSettingsProp = false,
  hideReportTemplate: hideReportTemplateProp = false,
  suggestionsList,
  placeholdersList,
  initialFiles,
  droppedFiles,
}: UploadModalProps) => {
  const isLibrary = mode === "library";
  const hasEvidenceIntegration = hasEvidenceIntegrationProp && !isLibrary;
  const hideReportSettings = hideReportSettingsProp || isLibrary;
  const hideReportTemplate = hideReportTemplateProp || isLibrary;
  const [files, setFiles] = useState<FileConfig[]>([]);
  const [error, setError] = useState<string>("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(true);
  const [expandedFileIndices, setExpandedFileIndices] = useState<number[]>([]);
  const [draggedFileIndex, setDraggedFileIndex] = useState<number | null>(null);
  const [dragOverFileIndex, setDragOverFileIndex] = useState<number | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const evidenceModalRef = useRef<HTMLDialogElement>(null);
  const previousUploadsModalRef = useRef<HTMLDialogElement>(null);
  const recordingModalRef = useRef<HTMLDialogElement>(null);

  // Report-level options (in advanced options)
  const [includeTimestamps, setIncludeTimestamps] = useState(false);
  // Shared with AudioPlayback so the last-picked detail level is the default
  // everywhere
  const [detailLevel, setDetailLevel] = useLocalStorageState<
    "detailed" | "standard" | "concise"
  >("pnai-reportDetailLevel", "standard");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [templateKey, setTemplateKey] = useState(0);
  const [evidenceModalKey, setEvidenceModalKey] = useState(0);
  const [previousUploadsModalKey, setPreviousUploadsModalKey] = useState(0);

  // Per-report call sign override — defaults to the saved preference. The
  // input is only shown when the officer has call signs enabled and one set
  // in preferences.
  const { reportPreferences: userReportPreferences, addons } = useAuth();

  // Optional investigation context: entitled officers can point report
  // generation at one of their investigations, so names, aliases, and coded
  // language established there resolve correctly in the narrative.
  const investigationsEntitled = Boolean(addons?.investigations?.entitled);
  // Footage analysis (video tier): the toggle only shows for entitled officers.
  const videoEntitled = Boolean(addons?.video?.entitled) && !isLibrary;
  const [investigationOptions, setInvestigationOptions] = useState<{ id: string; name: string; case_number: string | null }[]>([]);
  const [selectedInvestigationId, setSelectedInvestigationId] = useState<string | null>(null);
  useEffect(() => {
    if (!investigationsEntitled) return;
    axios
      .get<{ id: string; name: string; case_number: string | null }[]>("/api/investigations")
      .then((r) => setInvestigationOptions(r.data.map((i) => ({ id: i.id, name: i.name, case_number: i.case_number }))))
      .catch(() => setInvestigationOptions([]));
  }, [investigationsEntitled]);
  const preferenceCallSign =
    (userReportPreferences?.include_call_sign &&
      userReportPreferences?.call_sign) ||
    "";
  const [callSign, setCallSign] = useState(preferenceCallSign);

  // reportPreferences loads async from AuthContext — populate the input with
  // the saved call sign once it arrives (not just as a placeholder)
  useEffect(() => {
    setCallSign(preferenceCallSign);
  }, [preferenceCallSign]);

  // Re-mount TemplateSelector, EvidenceModal, and PreviousUploadsModal when dialog opens fresh
  useEffect(() => {
    const dialog = modalRef.current;
    if (!dialog) return;
    const observer = new MutationObserver(() => {
      if (dialog.hasAttribute("open")) {
        setSelectedTemplateId(null);
        setSelectedInvestigationId(null);
        setTemplateKey((k) => k + 1);
        setEvidenceModalKey((k) => k + 1);
        setPreviousUploadsModalKey((k) => k + 1);
        setCallSign(preferenceCallSign);
      }
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ["open"] });
    return () => observer.disconnect();
  }, [modalRef, preferenceCallSign]);

  useEffect(() => {
    if (!initialFiles || initialFiles.length === 0) return;
    setFiles(initialFiles);
    setExpandedFileIndices(initialFiles.map((_, i) => i));
    setError("");
  }, [initialFiles]);

  const resetModal = () => {
    setFiles([]);
    setError("");
    setShowAdvancedOptions(false);
    setExpandedFileIndices([]);
    setIncludeTimestamps(false);
    setDetailLevel("standard");
    setSelectedTemplateId(null);
    setSelectedInvestigationId(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    evidenceModalRef.current?.close();
    previousUploadsModalRef.current?.close();
    recordingModalRef.current?.close();
  };

  const handleRecordingAdded = (blob: Blob) => {
    const remainingSlots = MAX_FILES - files.length;
    if (remainingSlots <= 0) {
      setError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const file = new File([blob], `recording-${Date.now()}.wav`, {
      type: "audio/wav",
    });

    const newFileConfig: FileConfig = {
      file,
      // Saved recordings default to a readable name the officer can change.
      displayName: isLibrary ? defaultRecordingName() : undefined,
      mediaType: "audio",
      audioType: "narrative",
      speakers: [],
      languages: [],
      additionalContext: "",
    };

    const newFiles = [...files, newFileConfig];
    setFiles(newFiles);
    setExpandedFileIndices([...expandedFileIndices, newFiles.length - 1]);
  };

  const handleEvidenceSelected = (evidence: EvidenceItem) => {
    const remainingSlots = MAX_FILES - files.length;
    if (remainingSlots <= 0) {
      setError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    // Pre-fill additional context with Axon evidence metadata
    const contextParts: string[] = [];
    if (evidence.title) contextParts.push(`File Name: ${evidence.title}`);
    if (evidence.externalId)
      contextParts.push(`Case #: ${evidence.externalId}`);
    if (evidence.recordedOn)
      contextParts.push(
        `Recorded: ${new Date(evidence.recordedOn).toLocaleString()}`,
      );
    if (evidence.owner)
      contextParts.push(
        `Officer: ${evidence.owner}${evidence.ownerBadge ? ` #${evidence.ownerBadge}` : ""}`,
      );

    const newFileConfig: FileConfig = {
      file: new File([], evidence.title || "evidence", {
        type: evidence.contentType,
      }),
      mediaType: "video",
      audioType: "interaction",
      speakers: [],
      languages: [],
      additionalContext: contextParts.join("\n"),
      axonSource: {
        evidenceId: evidence.id,
        title: evidence.title,
        size: evidence.size,
        duration: evidence.duration,
        contentType: evidence.contentType,
      },
    };

    const newFiles = [...files, newFileConfig];
    setFiles(newFiles);
    setExpandedFileIndices([...expandedFileIndices, newFiles.length - 1]);
    evidenceModalRef.current?.close();
  };

  const handlePreviousUploadSelected = (item: PreviousUploadItem) => {
    const remainingSlots = MAX_FILES - files.length;
    if (remainingSlots <= 0) {
      setError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    const newFileConfig = fileConfigFromLibraryItem({
      id: item.id,
      source: item.source ?? "case",
      kind: "audio",
      fileName: item.fileName,
      fileSize: item.fileSize,
      fileType: item.fileType,
      fileUrl: item.fileUrl,
      metadata: item.metadata,
    });

    const newFiles = [...files, newFileConfig];
    setFiles(newFiles);
    setExpandedFileIndices([...expandedFileIndices, newFiles.length - 1]);
    previousUploadsModalRef.current?.close();
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const getMediaDuration = (
    file: File,
    mediaType: MediaType,
  ): Promise<number> => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);

      if (mediaType === "video") {
        const video = document.createElement("video");
        video.addEventListener("loadedmetadata", () => {
          URL.revokeObjectURL(url);
          resolve(video.duration);
        });
        video.addEventListener("error", (e) => {
          URL.revokeObjectURL(url);
          reject(e);
        });
        video.src = url;
      } else {
        const audio = new Audio();
        audio.addEventListener("loadedmetadata", () => {
          URL.revokeObjectURL(url);
          resolve(audio.duration);
        });
        audio.addEventListener("error", (e) => {
          URL.revokeObjectURL(url);
          reject(e);
        });
        audio.src = url;
      }
    });
  };

  const isValidFile = async (file: File): Promise<boolean> => {
    const mediaType = getMediaTypeFromFile(file);
    const ext = file.name.split(".").pop()?.toLowerCase();

    // PDFs, images, Word/text files and spreadsheets
    if (mediaType === "image" || mediaType === "document") {
      if (!allowDocuments) {
        setError(`${file.name}: Only audio and MP4 video files can be added here.`);
        return false;
      }
      const kind = documentKindOf(file.name);
      if (!kind) {
        setError(`${file.name}: Supported files are PDF, Word, text, JPG, PNG, WebP, Excel, and CSV.`);
        return false;
      }
      if (file.size > DOCUMENT_MAX_MB[kind] * 1024 * 1024) {
        setError(`${file.name}: File size must be less than ${DOCUMENT_MAX_MB[kind]}MB`);
        return false;
      }
      return true;
    }

    // Only allow MP4 for video files
    if (mediaType === "video" && ext !== "mp4" && file.type !== "video/mp4") {
      setError(`${file.name}: Only MP4 video files are supported.`);
      return false;
    }

    // For audio and video files, validate duration (no size limit for video since audio is extracted)
    if (mediaType === "audio" || mediaType === "video") {
      // Audio-only files still have a size limit
      if (mediaType === "audio") {
        const MAX_FILE_SIZE = MAX_INTERACTION_FILE_SIZE_IN_MB * 1024 * 1024;
        if (file.size > MAX_FILE_SIZE) {
          setError(
            `${file.name}: File size must be less than ${MAX_INTERACTION_FILE_SIZE_IN_MB / 1000}GB`,
          );
          return false;
        }
      }

      // Check duration for audio/video
      try {
        const duration = await getMediaDuration(file, mediaType);
        if (duration > MAX_AUDIO_DURATION_SECONDS) {
          setError(
            `${file.name}: ${mediaType === "audio" ? "Audio" : "Video"} duration must be less than ${MAX_AUDIO_DURATION_HOURS} hours`,
          );
          return false;
        }
      } catch (err) {
        console.error(`Error checking ${mediaType} duration:`, err);
        // Don't block upload if we can't check duration
      }
    }

    return true;
  };

  const MAX_FILES = 10;

  // Validate, cap at MAX_FILES, and append: shared by the drop zone, the file
  // picker, and files dropped on the page behind the modal (`droppedFiles`).
  const addRawFiles = async (incoming: File[]) => {
    setError("");
    const remainingSlots = MAX_FILES - files.length;

    if (remainingSlots <= 0) {
      setError(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    if (incoming.length > remainingSlots) {
      setError(
        `Can only add ${remainingSlots} more file${remainingSlots === 1 ? "" : "s"} (maximum ${MAX_FILES})`,
      );
    }

    const filesToProcess = incoming.slice(0, remainingSlots);
    const validFiles: File[] = [];
    for (const file of filesToProcess) {
      if (await isValidFile(file)) {
        validFiles.push(file);
      }
    }

    // Add all valid files at once
    if (validFiles.length > 0) {
      const newFileConfigs = validFiles.map((file) => {
        const mediaType = getMediaTypeFromFile(file);
        // Auto-set to interaction if: exceeds narrative size limit, over 75MB, or video file
        const shouldBeInteraction =
          (mediaType === "audio" || mediaType === "video") &&
          (exceedsNarrativeSizeLimit(file) ||
            file.size > 75 * 1024 * 1024 ||
            mediaType === "video");
        return {
          file,
          mediaType,
          audioType: shouldBeInteraction
            ? ("interaction" as const)
            : ("narrative" as const),
          speakers: [],
          languages: [],
          additionalContext: "",
        };
      });
      setFiles([...files, ...newFileConfigs]);
      // Auto-expand all newly added files
      const newIndices = validFiles.map((_, i) => files.length + i);
      setExpandedFileIndices([...expandedFileIndices, ...newIndices]);
    }
  };

  // Files dropped on the page behind the modal (the Files list): the page opens
  // the dialog and hands them over here. Each new array is one drop.
  useEffect(() => {
    if (!droppedFiles || droppedFiles.length === 0) return;
    void addRawFiles(droppedFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedFiles]);

  const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    await addRawFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    await addRawFiles(selected);
    // Reset input value to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (index: number) => {
    setError("");
    setFiles(files.filter((_, i) => i !== index));
    // Remove this index and decrement all indices greater than the removed index
    setExpandedFileIndices(
      expandedFileIndices
        .filter((i) => i !== index)
        .map((i) => (i > index ? i - 1 : i)),
    );
  };

  const updateFileConfig = (index: number, updates: Partial<FileConfig>) => {
    setError("");
    setFiles(
      files.map((fileConfig, i) =>
        i === index ? { ...fileConfig, ...updates } : fileConfig,
      ),
    );
  };

  const addSpeakerToFile = (fileIndex: number) => {
    setError("");
    const newSpeaker: Speaker = {
      name: "",
      role: "",
    };
    const fileConfig = files[fileIndex];
    updateFileConfig(fileIndex, {
      speakers: [...fileConfig.speakers, newSpeaker],
    });
  };

  const removeSpeakerFromFile = (fileIndex: number, speakerIndex: number) => {
    setError("");
    const fileConfig = files[fileIndex];
    updateFileConfig(fileIndex, {
      speakers: fileConfig.speakers.filter((_, i) => i !== speakerIndex),
    });
  };

  const updateSpeaker = (
    fileIndex: number,
    speakerIndex: number,
    field: keyof Speaker,
    value: string,
  ) => {
    setError("");
    const fileConfig = files[fileIndex];
    updateFileConfig(fileIndex, {
      speakers: fileConfig.speakers.map((speaker, i) =>
        i === speakerIndex ? { ...speaker, [field]: value } : speaker,
      ),
    });
  };

  const validateUpload = () => {
    setError("");

    if (files.length === 0) {
      setError("Please add at least one file");
      return false;
    }

    // Validate each file
    for (let i = 0; i < files.length; i++) {
      const fileConfig = files[i];
      // Validate speaker count for audio/video interaction files
      if (
        (fileConfig.mediaType === "audio" ||
          fileConfig.mediaType === "video") &&
        fileConfig.audioType === "interaction" &&
        fileConfig.speakers.length === 1
      ) {
        setError(
          `${fileConfig.file.name}: When specifying speakers, please add at least 2 speakers or remove speakers for AI auto-detection.`,
        );
        return false;
      }
    }

    return true;
  };

  const handleUpload = () => {
    if (validateUpload()) {
      const options: UploadOptions = {
        includeTimestamps,
        detailLevel,
        reportTemplateId: hideReportTemplate ? null : selectedTemplateId,
        callSign: preferenceCallSign ? callSign.trim() || null : null,
        investigationId: hideReportSettings || hideReportTemplate ? null : selectedInvestigationId,
      };
      onUpload(files, options);
      modalRef.current?.close();
      resetModal();
    }
  };

  const toggleFileExpanded = (index: number) => {
    if (expandedFileIndices.includes(index)) {
      setExpandedFileIndices(expandedFileIndices.filter((i) => i !== index));
    } else {
      setExpandedFileIndices([...expandedFileIndices, index]);
    }
  };

  const handleFileDragStart = (index: number) => {
    setDraggedFileIndex(index);
  };

  const handleFileDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverFileIndex(index);
  };

  const handleFileDragLeave = () => {
    setDragOverFileIndex(null);
  };

  const handleFileDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedFileIndex === null || draggedFileIndex === dropIndex) {
      setDraggedFileIndex(null);
      setDragOverFileIndex(null);
      return;
    }

    const newFiles = [...files];
    const draggedFile = newFiles[draggedFileIndex];
    newFiles.splice(draggedFileIndex, 1);
    newFiles.splice(dropIndex, 0, draggedFile);
    setFiles(newFiles);

    // Update expanded indices after reordering
    const newExpandedIndices = expandedFileIndices.map((expandedIdx) => {
      if (expandedIdx === draggedFileIndex) {
        return dropIndex;
      } else if (
        draggedFileIndex < dropIndex &&
        expandedIdx > draggedFileIndex &&
        expandedIdx <= dropIndex
      ) {
        return expandedIdx - 1;
      } else if (
        draggedFileIndex > dropIndex &&
        expandedIdx >= dropIndex &&
        expandedIdx < draggedFileIndex
      ) {
        return expandedIdx + 1;
      }
      return expandedIdx;
    });
    setExpandedFileIndices(newExpandedIndices);

    setDraggedFileIndex(null);
    setDragOverFileIndex(null);
  };

  const handleFileDragEnd = () => {
    setDraggedFileIndex(null);
    setDragOverFileIndex(null);
  };

  return (
    <dialog ref={modalRef} className="modal">
      <div className="modal-box max-w-2xl" data-tour="upload-modal">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-bold text-lg">{isLibrary ? "Add to Files" : "Upload Media Files"}</h3>
          <button onClick={() => modalRef.current?.close()}>
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Suggestions/placeholders list — shown in add-to-report mode */}
        {hideReportSettings &&
          suggestionsList &&
          (suggestionsList.filter((s) => !s.implemented).length > 0 ||
            (placeholdersList && placeholdersList.length > 0)) && (
            <div className="mb-4 max-h-[280px] overflow-y-scroll rounded-lg border border-base-300 bg-base-100">
              <div className="px-3 py-2 border-b border-slate-100 dark:border-base-300 sticky top-0 bg-base-100 z-10">
                <p className="text-xs font-medium text-slate-500 dark:text-base-content/60 m-0">
                  Upload supplemental audio and we'll intelligently integrate
                  new details into your report. Suggested points to cover.
                </p>
              </div>
              <div className="px-3 py-2 flex flex-col gap-1.5">
                {suggestionsList
                  .filter((s) => !s.implemented)
                  .map((s) => (
                    <div
                      key={s.id}
                      className="flex items-start gap-2 text-sm text-slate-600 dark:text-base-content/70"
                    >
                      <span className="text-blue-400 mt-0.5 shrink-0">●</span>
                      <SuggestionPoint content={s.content} />
                    </div>
                  ))}
                {placeholdersList?.map((p, i) => (
                  <div
                    key={`ph-${i}`}
                    className="flex items-start gap-2 text-sm text-slate-600 dark:text-base-content/70"
                  >
                    <span className="text-amber-500 mt-0.5 shrink-0 text-xs font-mono">
                      {"{ }"}
                    </span>
                    <span className="font-mono text-xs bg-amber-50 dark:bg-amber-950/40 px-1 py-0.5 rounded">
                      {p}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* File Upload Area */}
        <div
          data-tour="upload-dropzone"
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors hover:bg-gray-100 dark:hover:bg-base-200 cursor-pointer ${
            isDragOver ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40" : "border-gray-300 dark:border-base-300"
          }`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept={allowDocuments ? `${AUDIO_ACCEPT},${DOCUMENT_ACCEPT}` : AUDIO_ACCEPT}
            multiple
            className="hidden"
          />
          <CloudArrowUpIcon className="size-12 mx-auto text-blue-400" />
          <p className="text-gray-800 dark:text-base-content/80 text-md font-medium max-w-96 mx-auto">
            Choose files or drag and drop
          </p>
          <p className="text-base-content/60 text-sm max-w-96 mx-auto">
            {allowDocuments
              ? "Audio (MP3, MP4, M4A, WAV, under 4 hours), PDF, Word, images, Excel, CSV"
              : "MP3, MP4, M4A, WAV, under 4 hours"}
          </p>
        </div>

        <div
          className={cn("flex gap-2 mt-3", !hasEvidenceIntegration && "mt-3")}
        >
          <button
            type="button"
            onClick={() => recordingModalRef.current?.showModal()}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-base-300 rounded-lg text-sm font-medium text-gray-700 dark:text-base-content/80 hover:bg-base-200 transition-colors",
              files.length >= MAX_FILES && "opacity-50 cursor-not-allowed",
            )}
            disabled={files.length >= MAX_FILES}
          >
            <MicrophoneIcon className="h-5 w-5 text-blue-400" />
            Record Audio
          </button>
          {hasEvidenceIntegration && (
            <button
              type="button"
              data-tour="upload-bodycam"
              onClick={() => evidenceModalRef.current?.showModal()}
              className={cn(
                "flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-base-300 rounded-lg text-sm font-medium text-gray-700 dark:text-base-content/80 hover:bg-base-200 transition-colors",
                files.length >= MAX_FILES && "opacity-50 cursor-not-allowed",
              )}
              disabled={files.length >= MAX_FILES}
            >
              <VideoCameraIcon className="h-5 w-5 text-blue-400" />
              Add Bodycam Video
            </button>
          )}
          {!isLibrary && (
          <button
            type="button"
            data-tour="upload-previous"
            onClick={() => previousUploadsModalRef.current?.showModal()}
            className={cn(
              "flex-1 flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 dark:border-base-300 rounded-lg text-sm font-medium text-gray-700 dark:text-base-content/80 hover:bg-base-200 transition-colors",
              files.length >= MAX_FILES && "opacity-50 cursor-not-allowed",
            )}
            disabled={files.length >= MAX_FILES}
          >
            <ClockIcon className="h-5 w-5 text-blue-400" />
            Previous Uploads
          </button>
          )}
        </div>

        <div className="flex items-center justify-center gap-4 mt-2">
          <p className="text-base-content/40 text-xs text-center" data-tour="upload-compressor">
            Uploading large files?{" "}
            <a
              href="https://police-narratives-ai-compressor.s3.us-gov-west-1.amazonaws.com/PoliceNarrativesAudioExtractor_Setup.exe"
              className="text-blue-500 hover:text-blue-600 dark:text-blue-300 underline"
              onClick={(e) => e.stopPropagation()}
            >
              Download our file compressor
            </a>{" "}
            to reduce file size and dramatically speed up uploads.
          </p>
        </div>

        {error && (
          <div className="text-red-500 text-sm mt-2 text-center">{error}</div>
        )}

        {/* Files List */}
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-base-content/80 mb-3">
              Files ({files.length}){" "}
              {files.length > 1 && (
                <span className="text-base-content/60 font-normal">
                  • Drag to reorder chronologically
                </span>
              )}
            </p>
            {/* Audio/Video Type Descriptions - Only show if there are audio or video files */}
            {files.some(
              (f) => f.mediaType === "audio" || f.mediaType === "video",
            ) && (
              <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-3 text-xs space-y-2">
                <div>
                  <p className="text-blue-700 dark:text-blue-300">
                    <span className="font-bold">Narrative:</span> Single-speaker
                    recording of an officer's first-person account of events
                  </p>
                </div>
                <div>
                  <p className="text-purple-800 dark:text-purple-400">
                    <span className="font-bold">Interaction:</span>{" "}
                    Multi-speaker recording for interviews, witness statements,
                    or field recordings
                  </p>
                </div>
              </div>
            )}
            {files.map((fileConfig, index) => (
              <div
                key={index}
                data-tour="upload-file-row"
                data-file-index={index}
                draggable
                onDragStart={() => handleFileDragStart(index)}
                onDragOver={(e) => handleFileDragOver(e, index)}
                onDragLeave={handleFileDragLeave}
                onDrop={(e) => handleFileDrop(e, index)}
                onDragEnd={handleFileDragEnd}
                className={cn(
                  "border border-base-300 rounded-lg overflow-hidden transition-all",
                  draggedFileIndex === index && "opacity-50",
                  dragOverFileIndex === index &&
                    draggedFileIndex !== index &&
                    "bg-blue-50 dark:bg-blue-900/40 border-blue-400",
                )}
              >
                {/* File Header */}
                <div
                  className="flex items-center justify-between p-3 bg-base-200 hover:bg-gray-100 dark:hover:bg-base-300 cursor-pointer transition-colors"
                  onClick={() => toggleFileExpanded(index)}
                >
                  {files.length > 1 && (
                    <Bars3Icon className="h-5 w-5 text-base-content/40 mr-2 flex-shrink-0 cursor-move" />
                  )}
                  <div className="flex-1 min-w-0">
                    {fileConfig.previousUpload ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          <ClockIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <p className="text-sm font-medium text-gray-800 dark:text-base-content/80 truncate">
                            {fileConfig.previousUpload.fileName}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-1.5 py-0.5 bg-green-50 dark:bg-green-950/40 text-green-600 dark:text-green-400 rounded font-medium">
                            Previous Upload
                          </span>
                          <span className="text-xs text-base-content/60">
                            {fileConfig.previousUpload.fileSize}
                          </span>
                          {fileConfig.audioType !==
                            fileConfig.previousUpload.originalAudioType && (
                            <span className="text-xs text-amber-600 dark:text-amber-400">
                              Will re-transcribe as {fileConfig.audioType}
                            </span>
                          )}
                        </div>
                      </>
                    ) : fileConfig.axonSource ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          <VideoCameraIcon className="h-4 w-4 text-blue-500 dark:text-blue-300 flex-shrink-0" />
                          <p className="text-sm font-medium text-gray-800 dark:text-base-content/80 truncate">
                            {fileConfig.axonSource.title || "Untitled"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs px-1.5 py-0.5 bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 rounded font-medium">
                            Axon Evidence
                          </span>
                          <span className="text-xs text-base-content/60">
                            {(fileConfig.axonSource.size / 1024 / 1024).toFixed(
                              0,
                            )}{" "}
                            MB
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-gray-800 dark:text-base-content/80 truncate">
                          {fileConfig.file.name}
                        </p>
                        <p className="text-xs text-base-content/60">
                          {(fileConfig.file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </>
                    )}
                  </div>
                  <div
                    className="flex items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Kind badge for non-audio files (they are read, not transcribed) */}
                    {(fileConfig.mediaType === "image" ||
                      fileConfig.mediaType === "document") && (
                      <span className="px-2 py-1 text-xs rounded-md bg-gray-100 dark:bg-base-300 text-gray-700 dark:text-base-content/80">
                        {fileConfig.mediaType === "image"
                          ? "Image"
                          : documentKindOf(fileConfig.file.name) === "spreadsheet"
                            ? "Spreadsheet"
                            : "Document"}
                      </span>
                    )}
                    {/* Audio/Video Type Selector - For audio and video files */}
                    {(fileConfig.mediaType === "audio" ||
                      fileConfig.mediaType === "video") &&
                      (fileConfig.axonSource ? (
                        <Tooltip
                          overlay={
                            <span>
                              Body cam footage is always processed as
                              interaction audio
                            </span>
                          }
                          placement="top"
                        >
                          <span
                            className={cn(
                              "border h-auto text-xs cursor-default",
                              "bg-purple-100 dark:bg-purple-950/40 text-purple-800 dark:text-purple-400 border-purple-500",
                              "px-2.5 py-1 rounded-md font-medium",
                            )}
                          >
                            Interaction
                          </span>
                        </Tooltip>
                      ) : (
                        <div className="flex gap-1">
                          {exceedsNarrativeSizeLimit(fileConfig.file) ? (
                            <Tooltip
                              overlay={
                                <span>
                                  File exceeds {MAX_NARRATIVE_FILE_SIZE_IN_MB}MB
                                  limit for narrative processing
                                </span>
                              }
                              placement="top"
                            >
                              <button
                                type="button"
                                onClick={(e) => e.stopPropagation()}
                                data-tour="upload-type-narrative"
                                className={cn(
                                  "border h-auto text-xs cursor-not-allowed",
                                  "text-base-content/40 bg-base-200 border-base-300",
                                  "px-2.5 py-1 rounded-md font-medium",
                                )}
                              >
                                Narrative
                              </button>
                            </Tooltip>
                          ) : (
                            <Button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                updateFileConfig(index, {
                                  audioType: "narrative",
                                  libraryOther: false,
                                  speakers: [], // Reset speakers when changing type
                                });
                              }}
                              variant={
                                fileConfig.audioType === "narrative" && !fileConfig.libraryOther
                                  ? "secondary"
                                  : "outline"
                              }
                              className={cn(
                                "!border h-auto text-xs",
                                fileConfig.audioType === "narrative" && !fileConfig.libraryOther
                                  ? "border-blue-500"
                                  : "text-base-content/60 bg-base-100",
                                "px-2.5 py-1 rounded-md",
                              )}
                              size="xs"
                              data-tour="upload-type-narrative"
                            >
                              Narrative
                            </Button>
                          )}
                          <Button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateFileConfig(index, {
                                audioType: "interaction",
                                libraryOther: false,
                                speakers: [], // Reset speakers when changing type
                              });
                            }}
                            variant="outline"
                            className={cn(
                              "!border h-auto text-xs",
                              fileConfig.audioType === "interaction" && !fileConfig.libraryOther
                                ? "bg-purple-100 dark:bg-purple-950/40 text-purple-800 dark:text-purple-400 border-purple-500 hover:bg-purple-200 dark:hover:bg-purple-950/60"
                                : "text-base-content/60 bg-base-100 hover:bg-purple-50 dark:hover:bg-purple-950/40",
                              "px-2.5 py-1 rounded-md",
                            )}
                            size="xs"
                            data-tour="upload-type-interaction"
                          >
                            Interaction
                          </Button>
                          {/* Saved to Files only: keep the audio without transcribing it */}
                          {isLibrary && (
                            <Tooltip overlay={<span>Save without transcribing</span>} placement="top">
                              <Button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  updateFileConfig(index, { libraryOther: true, audioType: "narrative", speakers: [] });
                                }}
                                variant="outline"
                                className={cn(
                                  "!border h-auto text-xs",
                                  fileConfig.libraryOther
                                    ? "bg-base-300 text-gray-700 dark:text-base-content/80 border-gray-500 dark:border-base-content/40"
                                    : "text-base-content/60 bg-base-100 hover:bg-base-200",
                                  "px-2.5 py-1 rounded-md",
                                )}
                                size="xs"
                              >
                                Other
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                      ))}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeFile(index);
                      }}
                      className="p-1 text-red-600 hover:text-red-700 dark:text-red-400"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFileExpanded(index);
                      }}
                      className="p-1"
                    >
                      {expandedFileIndices.includes(index) ? (
                        <ChevronUpIcon className="h-4 w-4 text-base-content/60" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-base-content/60" />
                      )}
                    </button>
                  </div>
                </div>

                {/* File Configuration (Expanded) */}
                <div
                  className={cn(
                    "transition-all duration-300 ease-in-out overflow-hidden",
                    expandedFileIndices.includes(index)
                      ? "max-h-[1000px] opacity-100"
                      : "max-h-0 opacity-0",
                  )}
                >
                  <div className="p-4 space-y-3 divide-y divide-base-200 bg-base-100 border-t [&>*:not(:first-child)]:pt-3">
                    {/* Name — files saved to the library can be named */}
                    {isLibrary && !fileConfig.previousUpload && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          value={fileConfig.displayName ?? baseName(fileConfig.file.name)}
                          onChange={(e) => updateFileConfig(index, { displayName: e.target.value })}
                          placeholder={baseName(fileConfig.file.name)}
                          maxLength={150}
                          className="px-3 py-2 text-sm border border-gray-300 dark:border-base-300 rounded-lg w-full bg-base-100"
                        />
                      </div>
                    )}

                    {/* Media Preview */}
                    {fileConfig.previousUpload ? (
                      fileConfig.mediaType === "audio" ||
                      fileConfig.mediaType === "video" ? (
                        <PreviousUploadPreview
                          fileId={fileConfig.previousUpload.caseFileId}
                        />
                      ) : null
                    ) : fileConfig.axonSource ? (
                      <AxonPreview
                        evidenceId={fileConfig.axonSource.evidenceId}
                      />
                    ) : (
                      <MediaPreview
                        file={fileConfig.file}
                        mediaType={fileConfig.mediaType}
                      />
                    )}

                    {/* Speaker Information - For interaction audio/video */}
                    {(fileConfig.mediaType === "audio" ||
                      fileConfig.mediaType === "video") &&
                      fileConfig.audioType === "interaction" && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label
                              data-tour="upload-speaker-information"
                              className="block text-sm font-medium text-gray-700 dark:text-base-content/80"
                            >
                              Speaker Information
                            </label>
                            <button
                              type="button"
                              data-tour="upload-add-speaker"
                              onClick={() => addSpeakerToFile(index)}
                              className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300"
                            >
                              <PlusIcon className="h-4 w-4" />
                              Add Speaker
                            </button>
                          </div>

                          {fileConfig.speakers.length === 0 ? (
                            <p className="text-xs text-base-content/60 italic">
                              Add speakers to identify voices in your recording.
                              List all speakers, or none. Only entered names
                              will be used. If left blank, the AI will do its
                              best to identify speakers from the transcription.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {fileConfig.speakers.map(
                                (speaker, speakerIndex) => (
                                  <div
                                    key={speakerIndex}
                                    className="flex gap-2 items-center"
                                  >
                                    <input
                                      type="text"
                                      placeholder="Name"
                                      value={speaker.name}
                                      onChange={(e) =>
                                        updateSpeaker(
                                          index,
                                          speakerIndex,
                                          "name",
                                          e.target.value,
                                        )
                                      }
                                      className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-base-300 rounded-lg"
                                    />
                                    <div className="flex gap-1">
                                      <select
                                        value={
                                          speaker.role.startsWith("other:")
                                            ? "other"
                                            : speaker.role || ""
                                        }
                                        onChange={(e) => {
                                          if (e.target.value === "other") {
                                            updateSpeaker(
                                              index,
                                              speakerIndex,
                                              "role",
                                              "other:",
                                            );
                                          } else {
                                            updateSpeaker(
                                              index,
                                              speakerIndex,
                                              "role",
                                              e.target.value,
                                            );
                                          }
                                        }}
                                        className="px-2 py-1 text-sm border border-gray-300 dark:border-base-300 rounded-lg"
                                      >
                                        <option value="">Select role</option>
                                        <option value="officer">Officer</option>
                                        <option value="detective">
                                          Detective
                                        </option>
                                        <option value="investigator">
                                          Investigator
                                        </option>
                                        <option value="victim">Victim</option>
                                        <option value="suspect">Suspect</option>
                                        <option value="witness">Witness</option>
                                        <option value="complainant">
                                          Complainant
                                        </option>
                                        <option value="other">Other</option>
                                      </select>
                                      {speaker.role.startsWith("other:") && (
                                        <input
                                          type="text"
                                          placeholder="Custom role"
                                          value={speaker.role.replace(
                                            "other:",
                                            "",
                                          )}
                                          onChange={(e) =>
                                            updateSpeaker(
                                              index,
                                              speakerIndex,
                                              "role",
                                              `other:${e.target.value}`,
                                            )
                                          }
                                          className="px-2 py-1 text-sm border border-gray-300 dark:border-base-300 rounded-lg w-24"
                                        />
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeSpeakerFromFile(
                                          index,
                                          speakerIndex,
                                        )
                                      }
                                      className="p-1 text-red-600 hover:text-red-700 dark:text-red-400"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  </div>
                                ),
                              )}
                            </div>
                          )}
                        </div>
                      )}

                    {/* Spoken Languages - For interaction audio/video */}
                    {(fileConfig.mediaType === "audio" ||
                      fileConfig.mediaType === "video") &&
                      fileConfig.audioType === "interaction" && (
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label
                              data-tour="upload-spoken-languages"
                              className="block text-sm font-medium text-gray-700 dark:text-base-content/80"
                            >
                              Spoken Languages
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                updateFileConfig(index, {
                                  languages: [...fileConfig.languages, ""],
                                });
                              }}
                              className="flex items-center gap-1 px-2 py-1 text-sm text-blue-600 hover:text-blue-700 dark:text-blue-300"
                            >
                              <PlusIcon className="h-4 w-4" />
                              Add Language
                            </button>
                          </div>

                          {fileConfig.languages.length === 0 ? (
                            <p className="text-xs text-base-content/60 italic">
                              Add spoken languages to improve transcription
                              accuracy. If left blank, language will be
                              auto-detected.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {fileConfig.languages.map((lang, langIndex) => (
                                <div
                                  key={langIndex}
                                  className="flex gap-2 items-center"
                                >
                                  <select
                                    value={lang}
                                    onChange={(e) => {
                                      const newLangs = [
                                        ...fileConfig.languages,
                                      ];
                                      newLangs[langIndex] = e.target.value;
                                      updateFileConfig(index, {
                                        languages: newLangs,
                                      });
                                    }}
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-base-300 rounded-lg"
                                  >
                                    <option value="">Select language</option>
                                    {TRANSCRIBE_LANGUAGES.map((l) => (
                                      <option key={l.code} value={l.code}>
                                        {l.name}
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newLangs =
                                        fileConfig.languages.filter(
                                          (_, i) => i !== langIndex,
                                        );
                                      updateFileConfig(index, {
                                        languages: newLangs,
                                      });
                                    }}
                                    className="p-1 text-red-600 hover:text-red-700 dark:text-red-400"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                    {/* Footage analysis (video files): read the frames, not just the audio. */}
                    {fileConfig.mediaType === "video" && videoEntitled && (
                      <div data-tour="upload-video-analysis" className="rounded-lg border border-base-300 bg-base-200/40 p-3 space-y-2">
                        <Toggle
                          label="Analyze the footage"
                          checked={Boolean(fileConfig.videoAnalysis)}
                          onChange={(e) =>
                            updateFileConfig(index, {
                              videoAnalysis: e.target.checked ? { mode: "verify", analyzeAll: false } : undefined,
                              // Analyzed footage keeps its original file (the video is the record).
                              ...(e.target.checked && { keepVideo: true }),
                            })
                          }
                          labelSize="xs"
                        />
                        <p className="text-xs text-base-content/60 italic">
                          The video is read frame by frame where something happens: a timestamped record of what the camera shows, and a footage check of the draft against it.
                        </p>
                        {fileConfig.videoAnalysis && (
                          <div className="space-y-1.5">
                            {(
                              [
                                ["verify", "Check the draft against the footage", "The narrative is written from the audio as usual; differences from the footage arrive as suggestions you accept or dismiss."],
                                ["enrich", "Write footage observations into the draft", "The narrative waits for the footage read and includes what the camera shows as body-camera observations. Use this for silent footage."],
                              ] as const
                            ).map(([mode, label, hint]) => {
                              const row = (
                                <label
                                className="flex items-start gap-2 cursor-pointer"
                              >
                                <input
                                  type="radio"
                                  name={`video-mode-${index}`}
                                  className="radio radio-xs mt-0.5 [--chkbg:theme(colors.blue.500)] checked:border-blue-500"
                                  checked={fileConfig.videoAnalysis?.mode === mode}
                                  onChange={() => updateFileConfig(index, { videoAnalysis: { mode, analyzeAll: fileConfig.videoAnalysis?.analyzeAll || false } })}
                                />
                                <span>
                                  <span className="text-xs font-medium text-base-content">{label}</span>
                                  <span className="block text-[11px] text-base-content/60">{hint}</span>
                                </span>
                              </label>
                              );
                              return mode === "verify" ? (
                                <div key={mode} data-tour="upload-video-verify">{row}</div>
                              ) : (
                                <div key={mode} data-tour="upload-video-enrich">{row}</div>
                              );
                            })}
                            <label className="flex items-center gap-2 cursor-pointer pt-1">
                              <input
                                type="checkbox"
                                className="checkbox checkbox-xs [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500"
                                checked={Boolean(fileConfig.videoAnalysis?.analyzeAll)}
                                onChange={(e) => updateFileConfig(index, { videoAnalysis: { mode: fileConfig.videoAnalysis?.mode || "verify", analyzeAll: e.target.checked } })}
                              />
                              <span className="text-xs text-base-content/70">Read every minute, not just where something happens (slower, costs more)</span>
                            </label>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Keep Video toggle - only for video files under 2GB */}
                    {fileConfig.mediaType === "video" &&
                      (fileConfig.axonSource
                        ? fileConfig.axonSource.size
                        : fileConfig.file.size) <=
                        2000 * 1024 * 1024 && (
                        <div data-tour="upload-keep-video">
                          <Toggle
                            label="Keep original video file"
                            checked={fileConfig.keepVideo || Boolean(fileConfig.videoAnalysis)}
                            disabled={Boolean(fileConfig.videoAnalysis)}
                            onChange={(e) =>
                              updateFileConfig(index, {
                                keepVideo: e.target.checked,
                              })
                            }
                            labelSize="xs"
                          />
                          <p className="text-xs text-base-content/60 italic">
                            {fileConfig.videoAnalysis
                              ? "Analyzed footage always keeps the original video file."
                              : "Keeping the video significantly slows down processing. Only enable if you need the original video file."}
                          </p>
                        </div>
                      )}

                    {/* Additional Context for this file */}
                    <TextArea
                      label="Additional Context (optional)"
                      value={fileConfig.additionalContext}
                      onChange={(e) =>
                        updateFileConfig(index, {
                          additionalContext: e.target.value,
                        })
                      }
                      placeholder="Add any additional context or instructions for this specific file..."
                      rows={2}
                      maxLength={1000}
                      labelSize="sm"
                      labelClassName="!min-h-7 mb-1"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Template selector — shown standalone in add-to-report mode */}
        {files.length > 0 && hideReportSettings && !hideReportTemplate && (
          <div className="mt-3 mb-2">
            <TemplateSelector
              key={templateKey}
              selectedTemplateId={selectedTemplateId}
              onSelect={setSelectedTemplateId}
            />
          </div>
        )}

        {/* Advanced Options Section (Report-level settings) — hidden in add-to-report mode */}
        {files.length > 0 && !hideReportSettings && !hideReportTemplate && (
          <div className="mt-4 border-t pt-4">
            <button
              type="button"
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              data-tour="upload-report-settings"
              className="flex items-center justify-between w-full p-2 text-left text-sm font-medium text-gray-700 dark:text-base-content/80"
            >
              Report Settings
              {showAdvancedOptions ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )}
            </button>

            <div
              className={cn(
                "transition-all duration-300 ease-in-out overflow-hidden",
                showAdvancedOptions
                  ? "max-h-[500px] opacity-100"
                  : "max-h-0 opacity-0",
              )}
            >
              <div className="mt-4 space-y-4 p-4 bg-base-200 rounded-lg">
                {/* Report Template */}
                <div data-tour="upload-template">
                  <TemplateSelector
                    key={templateKey}
                    selectedTemplateId={selectedTemplateId}
                    onSelect={setSelectedTemplateId}
                  />
                </div>

                {/* Investigation context — investigations add-on only */}
                {investigationsEntitled && investigationOptions.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                      Investigation Context
                    </label>
                    <select
                      className="select select-bordered bg-base-100 w-full text-sm font-normal"
                      value={selectedInvestigationId || ""}
                      onChange={(e) => setSelectedInvestigationId(e.target.value || null)}
                    >
                      <option value="">None</option>
                      {investigationOptions.map((inv) => (
                        <option key={inv.id} value={inv.id}>
                          {inv.name}
                          {inv.case_number ? ` (case ${inv.case_number})` : ""}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-base-content/60">
                      Names, aliases, and coded language established in the investigation help the AI understand this recording. Findings
                      from the investigation are not written into the report.
                    </p>
                  </div>
                )}

                {/* Call Sign Override — only when a call sign is set in preferences */}
                {preferenceCallSign && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                      Call Sign for This Report
                    </label>
                    <input
                      data-tour="upload-call-sign"
                      type="text"
                      value={callSign}
                      onChange={(e) => setCallSign(e.target.value)}
                      placeholder={preferenceCallSign}
                      className="px-3 py-2 text-sm border border-gray-300 dark:border-base-300 rounded-lg w-full bg-base-100"
                    />
                    <p className="mt-1 text-xs text-base-content/60">
                      Defaults to your saved call sign. Adjust it if you're on a
                      different shift or assignment for this report.
                    </p>
                  </div>
                )}

                {/* Detail Level Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-2">
                    Report Detail Level
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      {
                        value: "concise",
                        label: "Concise",
                        desc: "Brief summary",
                      },
                      {
                        value: "standard",
                        label: "Standard",
                        desc: "Balanced detail",
                      },
                      {
                        value: "detailed",
                        label: "Detailed",
                        desc: "Comprehensive",
                      },
                    ].map((option) => (
                      <Button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setError("");
                          setDetailLevel(option.value as any);
                        }}
                        variant={
                          detailLevel === option.value ? "secondary" : "outline"
                        }
                        className={cn(
                          "w-full !border h-auto",
                          detailLevel === option.value && "border-blue-500",
                          "p-3",
                        )}
                        size="sm"
                      >
                        <div>
                          <div className="font-medium">{option.label}</div>
                          <div className="text-xs font-normal">
                            {option.desc}
                          </div>
                        </div>
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Timestamps Toggle */}
                <div>
                  <Toggle
                    label="Include timestamps in narrative"
                    checked={includeTimestamps}
                    onChange={(e) => {
                      setError("");
                      setIncludeTimestamps(e.target.checked);
                    }}
                    labelSize="sm"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="modal-action">
          <Button
            onClick={handleUpload}
            data-tour="upload-submit"
            disabled={files.length === 0 || error !== ""}
            variant="primary"
            className="w-full"
          >
            {isLibrary ? "Save to Files" : "Upload"}{" "}
            {files.length > 0 &&
              `(${files.length} file${files.length > 1 ? "s" : ""})`}
          </Button>
        </div>
      </div>
      {hasEvidenceIntegration && (
        <EvidenceModal
          key={evidenceModalKey}
          modalRef={evidenceModalRef as React.RefObject<HTMLDialogElement>}
          onSelect={handleEvidenceSelected}
          selectOnly
          selectedEvidenceIds={files
            .filter((f) => f.axonSource?.evidenceId)
            .map((f) => f.axonSource!.evidenceId)}
        />
      )}
      <PreviousUploadsModal
        key={`prev-${previousUploadsModalKey}`}
        modalRef={previousUploadsModalRef as React.RefObject<HTMLDialogElement>}
        onSelect={handlePreviousUploadSelected}
        selectedFileIds={files
          .filter((f) => f.previousUpload?.caseFileId)
          .map((f) => f.previousUpload!.caseFileId)}
      />
      <AudioPlayback
        modalRef={recordingModalRef as React.RefObject<HTMLDialogElement>}
        handleAudioFileChange={async () => {}}
        addToUploadMode
        onAddToUpload={handleRecordingAdded}
        suggestions={suggestionsList}
        placeholders={placeholdersList}
      />
    </dialog>
  );
};

export default UploadModal;
